import React from "react";
import DropdownSelector from "../../../../../Common/dropdown-selector";
import MoreOptionsMenu from "./more-options-menu.component";
import styles from "./index.module.scss";
import { IDistributorPricing } from "../../../../../../common/validations/add-plan.validations";

interface ICurrencyItem {
  label: string;
  value: string;
}

interface IPeriodItem {
  label: string;
  value: string;
}

interface DistributorPricingProps {
  distributorPricing: IDistributorPricing;
  onDelete: () => void;
  onPricingChange: (pricing: Partial<IDistributorPricing>) => void;
}

const CURRENCY_OPTIONS: ICurrencyItem[] = [
  { label: "GBP", value: "GBP" },
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
];

const PERIOD_OPTIONS: IPeriodItem[] = [
  { label: "/month", value: "/month" },
  { label: "/quarter", value: "/quarter" },
  { label: "/year", value: "/year" },
];

const DistributorPricing = ({ distributorPricing, onDelete, onPricingChange }: DistributorPricingProps) => {
  const { distributor, currency, costPeriod, costValue, marginPercent, pricePeriod } = distributorPricing;

  const selectedCurrency = CURRENCY_OPTIONS.find(c => c.value === currency) || CURRENCY_OPTIONS[0];
  const selectedCostPeriod = PERIOD_OPTIONS.find(p => p.value === costPeriod) || PERIOD_OPTIONS[0];

  const handleCurrencyChange = (item: ICurrencyItem) => {
    onPricingChange({ currency: item.value });
  };

  const handleCostPeriodChange = (item: IPeriodItem) => {
    onPricingChange({ costPeriod: item.value });
  };

  const handleDuplicate = () => {
    // TODO: Implement duplicate functionality
    console.log("Duplicate clicked");
  };

  const handleDelete = () => {
    onDelete();
  };

  const calculateMarginValue = () => {
    const cost = Number.parseFloat(costValue);
    const margin = Number.parseFloat(marginPercent);
    if (!isNaN(cost) && !isNaN(margin)) {
      return ((cost * margin) / 100).toFixed(2);
    }
    return "0.00";
  };

  const calculatePrice = () => {
    const cost = Number.parseFloat(costValue);
    const margin = Number.parseFloat(marginPercent);
    if (!isNaN(cost) && !isNaN(margin)) {
      return (cost + (cost * margin) / 100).toFixed(2);
    }
    return "0.00";
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleWrapper}>
          <div className={styles.titleIconWrapper}>
            <img src={distributor.image} alt={distributor.name} />
          </div>

          <h1 className={styles.title}>
            {distributor.name}
          </h1>
        </div>

        <MoreOptionsMenu
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
      </header>

      <main className={styles.main}>
        <section className={styles.section}>
          <label
            htmlFor="cost-input"
            className={styles.label}
          >
            Cost
          </label>

          <DropdownSelector
            list={CURRENCY_OPTIONS}
            selected={selectedCurrency}
            onItemSelected={handleCurrencyChange}
            toggleClassName={styles.currencySelector}
            className={styles.dropdownWrapper}
          >
            <div className={styles.currencySelectorContent}>
              <span className={styles.currencyText}>
                {selectedCurrency.label}
              </span>
              <div className="arrow-icon" />
            </div>
          </DropdownSelector>

          <div className={styles.inputGroup}>
            <div className={styles.inputWrapper}>
              <div className={styles.inputContainer}>
                <input
                  id="cost-input"
                  type="text"
                  value={`£ ${costValue}`}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    onPricingChange({ costValue: value });
                  }}
                  className={styles.input}
                  aria-label="Cost value"
                />
              </div>
            </div>
          </div>

          <DropdownSelector
            list={PERIOD_OPTIONS}
            selected={selectedCostPeriod}
            onItemSelected={handleCostPeriodChange}
            toggleClassName={styles.periodSelector}
            className={styles.dropdownWrapper}
          >
            <div className={styles.periodSelectorContent}>
              <span className={styles.periodText}>
                {selectedCostPeriod.label}
              </span>
              <div className="arrow-icon" />
            </div>
          </DropdownSelector>
        </section>

        <section className={styles.marginSection}>
          <div className={styles.marginRow}>
            <label
              htmlFor="margin-input"
              className={styles.label}
            >
              Margin
            </label>

            <div className={styles.marginInputGroup}>
              <div className={styles.marginInputWrapper}>
                <div className={styles.marginInputContainer}>
                  <input
                    id="margin-input"
                    type="text"
                    value={marginPercent}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      onPricingChange({ marginPercent: value });
                    }}
                    className={styles.marginInput}
                    aria-label="Margin percentage"
                  />
                </div>

                <div className={styles.marginPercentWrapper}>
                  <div className={styles.marginPercentContent}>
                    <span className={styles.marginPercentText}>
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.marginValueWrapper}>
                <div className={styles.marginValueContainer}>
                  <output
                    htmlFor="margin-input cost-input"
                    className={styles.marginValue}
                    aria-label="Calculated margin value"
                  >
                    £ {calculateMarginValue()}
                  </output>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className={styles.divider} />

        <section className={styles.priceSection}>
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>
              Price
            </span>

            <div className={styles.priceValueWrapper}>
              <div className={styles.priceValueContainer}>
                <div className={styles.priceValueInner}>
                  <output
                    htmlFor="cost-input margin-input"
                    className={styles.priceValue}
                    aria-label="Calculated price"
                  >
                    £ {calculatePrice()}
                  </output>
                </div>
              </div>

              <div className={styles.pricePeriodSelector}>
                <div className={styles.pricePeriodContent}>
                  <span className={styles.pricePeriodText}>
                    {pricePeriod}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default DistributorPricing;